import { useContext, useState } from "react";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  UpOutlined
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Popover,
  Radio,
  Select,
  Tooltip,
  message
} from "antd";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  MUTATION_DELETE_VIEW_CONFIG,
  MUTATION_SAVE_VIEW_CONFIG,
  MUTATION_UPDATE_VIEW_CONFIG
} from "services/graphQL/mutations";
import { ProjectContext } from "context/ProjectProvider";
import { useMutation } from "@apollo/client";
import { QUERY_USER_CONFIG_VIEWS } from "services/graphQL/queries";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import styles from "./ColumnsViews.module.scss";
import { EFeaturePageId } from "../../constants";

export type TColumnType = {
  order: number;
  field: {
    name: string;
    filter: any;
    agFilter: string | null;
    sortOrder: "asc" | "desc" | null;
    range: any;
    width: number | null;
  };
  visibility: boolean;
};

export type TSaveColumnState = {
  columnList: Array<TColumnType>;
  quickSearch: string;
};

type Props = {
  setFilterStateFromData: (columnState: TSaveColumnState) => void;
  getColumnStateFromGrid: () => {
    columnList: any;
    quickSearch: string;
  } | null;
  afterApplyFilterCallBack?: Function;
  featureId: EFeaturePageId;
};

function ColumnConfigViews(props: Props) {
  const {
    setFilterStateFromData,
    getColumnStateFromGrid,
    afterApplyFilterCallBack,
    featureId
  } = props;
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [isCreatingView, setCreateViewUI] = useState(false);
  const [editingViewId, setEditingViewId] = useState<string | null>(null);
  const [editingViewName, setEditingViewName] = useState<string>("");
  const [saveOption, setSaveOption] = useState<"create" | "update">("create");
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const nameValue = Form.useWatch("name", form);

  const hide = () => {
    setOpen(false);
  };

  const { gqlClientForProject } = useContext(ProjectContext);

  const { data: viewsData, refetch: refetchViews } = useCIQQuery<{
    project_feature_user_view: Array<{
      id: string;
      name: string;
      column_config: TSaveColumnState;
    }>;
  }>(QUERY_USER_CONFIG_VIEWS, {
    client: gqlClientForProject,
    skip: !gqlClientForProject,
    variables: { feature_id: featureId }
  });

  const [saveViewColumnsConfig] = useMutation(MUTATION_SAVE_VIEW_CONFIG, {
    client: gqlClientForProject
  });

  const [updateViewColumnsConfig] = useMutation(MUTATION_UPDATE_VIEW_CONFIG, {
    client: gqlClientForProject
  });

  const [deleteViewColumnsConfig] = useMutation(MUTATION_DELETE_VIEW_CONFIG, {
    client: gqlClientForProject
  });

  const onViewApply = async (view: TSaveColumnState) => {
    setFilterStateFromData(view);
    hide();
    setTimeout(() => {
      if (afterApplyFilterCallBack) afterApplyFilterCallBack();
    }, 500);
  };

  const onCreateNewView = async (name: string) => {
    const currentColumnState = getColumnStateFromGrid() as any;
    if (currentColumnState) {
      const res = await saveViewColumnsConfig({
        variables: {
          name,
          feature_id: featureId,
          column_config: currentColumnState
        }
      });
      if (res.data) {
        message.success("Successfully added a view");
        refetchViews();
      } else if (res.errors) {
        message.error(res.errors[0].message);
      }
    }
  };

  const onUpdateExistingView = async (viewId: string) => {
    const currentColumnState = getColumnStateFromGrid() as any;
    const viewToUpdate = viewsData?.project_feature_user_view.find(
      (v) => v.id === viewId
    );

    if (!viewToUpdate) {
      message.error("View not found.");
      return;
    }

    if (currentColumnState) {
      const res = await updateViewColumnsConfig({
        variables: {
          id: viewId,
          data: {
            name: viewToUpdate.name,
            column_config: currentColumnState
          }
        }
      });
      if (res.data) {
        message.success(`Successfully updated '${viewToUpdate.name}'.`);
        refetchViews();
      } else if (res.errors) {
        message.error(res.errors[0].message);
      }
    }
  };

  const onDeleteView = (view: { id: string; name: string }) => {
    hide();
    Modal.confirm({
      title: null,
      icon: null,
      content: (
        <div className="flex items-start gap-3">
          <ExclamationCircleOutlined
            style={{ color: "#FAAD14", fontSize: 24, marginTop: 2 }}
          />
          <span className="text-base">
            Are you sure you want to delete this view?
          </span>
        </div>
      ),
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: {
        style: {
          backgroundColor: "#4448C1",
          borderColor: "#4448C1"
        }
      },
      cancelButtonProps: {
        style: {
          borderColor: "#D9D9D9"
        }
      },
      onOk: async () => {
        const res = await deleteViewColumnsConfig({
          variables: { id: view.id }
        });
        if (res.data) {
          message.success(`Successfully removed ${view.name}.`);
          refetchViews();
        } else if (res.errors) {
          message.error(res.errors[0].message);
        }
      }
    });
  };

  const onStartEdit = (view: { id: string; name: string }) => {
    setEditingViewId(view.id);
    setEditingViewName(view.name);
  };

  const onCancelEdit = () => {
    setEditingViewId(null);
    setEditingViewName("");
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (editingViewId) {
      onCancelEdit();
    }
    setCreateViewUI(false);
    setSaveOption("create");
    setSelectedViewId(null);
    form.resetFields();
    setOpen(newOpen);
  };

  const onUpdateViewName = async (viewId: string) => {
    const trimmedName = editingViewName.trim();

    if (!trimmedName) {
      return;
    }

    // Check for duplicate names
    const isDuplicateName =
      viewsData &&
      viewsData.project_feature_user_view.some(
        (v) => v.name.toLowerCase() === trimmedName.toLowerCase()
      );

    if (isDuplicateName || !trimmedName) {
      refetchViews();
      setEditingViewId(null);
      setEditingViewName("");
      return;
    }

    // Find the current view to get its column config
    const currentView = viewsData?.project_feature_user_view.find(
      (v) => v.id === viewId
    );

    if (!currentView) {
      message.error("View not found.");
      return;
    }

    const res = await updateViewColumnsConfig({
      variables: {
        id: viewId,
        data: {
          name: trimmedName,
          column_config: currentView?.column_config
        }
      }
    });

    if (res.data) {
      message.success("Successfully updated view name.");
      refetchViews();
      setEditingViewId(null);
      setEditingViewName("");
    } else if (res.errors) {
      message.error(res.errors[0].message);
    }
  };

  const listViewContent = (
    <div
      className={styles.boxStyle}
      style={{ borderRadius: 4, border: "none", overflow: "hidden" }}
    >
      <div className="px-4 py-3 font-semibold text-xs text-[#00000073]">
        {viewsData && viewsData?.project_feature_user_view?.length > 0
          ? "Saved views"
          : "Save Current View"}
      </div>
      <div className="max-h-64 overflow-y-auto">
        {viewsData &&
          viewsData.project_feature_user_view.map((v) => (
            <div
              key={v.id}
              className="w-full flex items-center px-4 py-3 justify-between bg-white hover:bg-gray-50 border-b"
            >
              {editingViewId === v.id ? (
                <>
                  <Input
                    value={editingViewName}
                    onChange={(e) => setEditingViewName(e.target.value)}
                    onPressEnter={() => onUpdateViewName(v.id)}
                    className="flex-1 mr-3"
                    autoFocus
                    data-testid="edit-view-name-input"
                  />
                  <div className="flex items-center space-x-3 flex-shrink-0">
                    <Tooltip title="Save">
                      <CheckOutlined
                        className="cursor-pointer text-[#4448C1] hover:text-[#4448C1] text-base"
                        onClick={() => onUpdateViewName(v.id)}
                        data-testid="button-save-edit"
                        style={{
                          backgroundColor: "#D9D9D9",
                          borderRadius: 6,
                          border: "1px solid #0000000F",
                          padding: 2,
                          width: 24,
                          height: 24
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <CloseOutlined
                        className="cursor-pointer text-[#4448C1] hover:text-[#4448C1] text-base"
                        onClick={onCancelEdit}
                        data-testid="button-cancel-edit"
                        style={{
                          backgroundColor: "#D9D9D9",
                          borderRadius: 6,
                          border: "1px solid #0000000F",
                          padding: 2,
                          width: 24,
                          height: 24
                        }}
                      />
                    </Tooltip>
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="flex-1 min-w-0 cursor-pointer text-sm text-[#000000E0] mr-3"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      onViewApply(v.column_config);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onViewApply(v.column_config);
                      }
                    }}
                  >
                    <TruncatedTextWithTooltip
                      text={v.name}
                      className="text-sm text-[#000000E0]"
                    />
                  </div>
                  <div className="flex items-center space-x-3 flex-shrink-0">
                    <Tooltip title="Rename">
                      <EditOutlined
                        className="cursor-pointer text-[#4448C1] hover:text-[#4448C1]"
                        onClick={() => onStartEdit(v)}
                        data-testid="button-edit"
                      />
                    </Tooltip>

                    <Tooltip title="Delete">
                      <DeleteOutlined
                        className="cursor-pointer text-red-500 hover:text-red-700"
                        onClick={() => {
                          onDeleteView(v);
                        }}
                        data-testid="button-delete"
                      />
                    </Tooltip>
                  </div>
                </>
              )}
            </div>
          ))}
      </div>
      <div className="px-4 py-3 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="text-[#000000A6]">
            <div className="font-semibold text-xs ">Save Current View</div>
            <div className="text-xs font-normal">
              Create new or update existing.
            </div>
          </div>
          <Button
            type="primary"
            onClick={() => {
              // Auto-select the appropriate save option based on current state
              if (
                viewsData &&
                viewsData.project_feature_user_view.length >= 10
              ) {
                setSaveOption("update");
              } else if (
                !viewsData ||
                viewsData.project_feature_user_view.length === 0
              ) {
                setSaveOption("create");
              } else {
                setSaveOption("create");
              }
              setCreateViewUI(true);
            }}
            data-testid="index-button"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );

  const saveViewModal = (
    <div className={styles.boxStyle} style={{ border: "none" }}>
      <Form
        form={form}
        layout="vertical"
        className="bg-white p-3"
        onFinish={(values) => {
          if (saveOption === "create") {
            const name = (values.name || "").trim();
            onCreateNewView(name);
          } else if (selectedViewId) {
            onUpdateExistingView(selectedViewId);
          } else {
            message.error("Please select a view to update.");
            return;
          }
          setCreateViewUI(false);
          setSaveOption("create");
          setSelectedViewId(null);
          form.resetFields();
        }}
      >
        <div className={`mb-6 ${styles.customRadioGroup}`}>
          <div className="font-semibold text-sm mb-3">
            How would you like to save the view?
          </div>
          <Radio.Group
            onChange={(e) => {
              setSaveOption(e.target.value);
              setSelectedViewId(null);
            }}
            value={saveOption}
          >
            <div className="space-y-3">
              <div>
                <Radio
                  value="create"
                  disabled={
                    viewsData &&
                    viewsData.project_feature_user_view.length >= 10
                  }
                >
                  <span className="text-sm">Create a new view</span>
                </Radio>
                {viewsData && viewsData.project_feature_user_view.length >= 10 && (
                  <div className="ml-6 p-0">
                    <Alert
                      message="Maximum of 10 views reached."
                      type="info"
                      showIcon
                      className="rounded-xl"
                    />
                  </div>
                )}
                {saveOption === "create" &&
                  (!viewsData ||
                    viewsData.project_feature_user_view.length < 10) && (
                    <Form.Item
                      className="ml-6"
                      label={
                        <span className="font-semibold text-xs">Name</span>
                      }
                      name="name"
                      required
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Please enter view name."
                        },
                        {
                          validator(rule, value) {
                            if (value) {
                              const name = value.trim().toLowerCase();
                              if (
                                viewsData &&
                                viewsData.project_feature_user_view.some(
                                  (v) => v.name.toLowerCase() === name
                                )
                              )
                                return Promise.reject(
                                  new Error("View name already exists.")
                                );
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <Input
                        placeholder="Enter name"
                        className="w-full"
                        data-testid="index-view-name-input"
                      />
                    </Form.Item>
                  )}
              </div>
              <div className="w-full">
                <Radio
                  value="update"
                  disabled={
                    !viewsData ||
                    viewsData.project_feature_user_view.length === 0
                  }
                >
                  <span className="text-sm">Update an existing view</span>
                </Radio>
                {saveOption === "update" && (
                  <Form.Item
                    className="ml-6"
                    label={
                      <span className="font-semibold text-xs">Select View</span>
                    }
                    required
                  >
                    <Select
                      placeholder="Select view"
                      value={selectedViewId}
                      onChange={(value) => setSelectedViewId(value)}
                      data-testid="index-view-select"
                      options={
                        viewsData?.project_feature_user_view.map((v) => ({
                          label: v.name,
                          value: v.id
                        })) || []
                      }
                    />
                  </Form.Item>
                )}
              </div>
            </div>
          </Radio.Group>
        </div>

        <div className="flex justify-end space-x-4">
          <Button
            onClick={() => {
              setCreateViewUI(false);
              setSaveOption("create");
              setSelectedViewId(null);
              form.resetFields();
            }}
            data-testid="index-button-cancel"
          >
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            data-testid="index-button-save"
            disabled={
              saveOption === "create"
                ? !nameValue || !nameValue.trim()
                : !selectedViewId
            }
          >
            Save
          </Button>
        </div>
      </Form>
    </div>
  );

  return (
    <Popover
      content={isCreatingView ? saveViewModal : listViewContent}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      overlayClassName={styles.popoverNoPadding}
      placement="bottomLeft"
      showArrow={false}
      data-testid="column-config-popover"
    >
      <Button data-testid="index-button-views">
        Views{" "}
        {open ? (
          <UpOutlined className="text-xs" />
        ) : (
          <DownOutlined className="text-xs" />
        )}
      </Button>
    </Popover>
  );
}
export default ColumnConfigViews;
